import React, { useState, useEffect, useRef } from 'react';
import { TableSkeleton } from '@/components/base/Skeleton';
import gisService from '@/modules/utility/gis/services/gisService';
import notificationService from '@/services/notificationService';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { TILE_URLS } from '@/config/mapTiles';

interface BaseLayer {
  id: number;
  layer_code: string;
  layer_name: string;
  layer_name_local: string;
  description: string;
  layer_type: string;
  layer_category: string;
  tile_url: string;
  tile_subdomains: string[];
  wms_url: string;
  wms_layers: string;
  wms_version: string;
  wms_format: string;
  wms_transparent: boolean;
  attribution: string;
  max_zoom: number;
  min_zoom: number;
  is_default: boolean;
  is_multi_select_enabled: boolean;
  parent_layer_id: number | null;
  icon: string;
  display_order: number;
  sequence_id: number;
  is_active: boolean;
}

interface FormData {
  layer_code: string;
  layer_name: string;
  layer_name_local: string;
  description: string;
  layer_type: string;
  layer_category: string;
  tile_url: string;
  tile_subdomains: string;
  wms_url: string;
  wms_layers: string;
  wms_version: string;
  wms_format: string;
  wms_transparent: boolean;
  attribution: string;
  max_zoom: number;
  min_zoom: number;
  is_default: boolean;
  is_multi_select_enabled: boolean;
  parent_layer_id: number | null;
  icon: string;
  display_order: number;
  sequence_id: number;
  is_active: boolean;
}

const BaseLayers: React.FC = () => {
  // State
  const [layers, setLayers] = useState<BaseLayer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<keyof BaseLayer>('sequence_id');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [editingLayer, setEditingLayer] = useState<BaseLayer | null>(null);
  const [deletingLayer, setDeletingLayer] = useState<BaseLayer | null>(null);
  const [previewLayer, setPreviewLayer] = useState<BaseLayer | null>(null);

  // Map preview ref
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);

  // Form state
  const [formData, setFormData] = useState<FormData>({
    layer_code: '',
    layer_name: '',
    layer_name_local: '',
    description: '',
    layer_type: 'tile',
    layer_category: 'street',
    tile_url: '',
    tile_subdomains: '',
    wms_url: '',
    wms_layers: '',
    wms_version: '1.1.1',
    wms_format: 'image/png',
    wms_transparent: true,
    attribution: '',
    max_zoom: 18,
    min_zoom: 0,
    is_default: false,
    is_multi_select_enabled: false,
    parent_layer_id: null,
    icon: 'ri-map-line',
    display_order: 0,
    sequence_id: 0,
    is_active: true
  });

  const itemsPerPage = 10;

  // Fetch data
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const response = await gisService.getAllBaseLayers();

      if (response.status === 200 && response.data) {
        setLayers(response.data);
      }
    } catch (error) {
      console.error('Error fetching base layers:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Category counts
  const streetCount = layers.filter(l => l.layer_category === 'street').length;
  const satelliteCount = layers.filter(l => l.layer_category === 'satellite').length;
  const hybridCount = layers.filter(l => l.layer_category === 'hybrid').length;
  const terrainCount = layers.filter(l => l.layer_category === 'terrain').length;
  const oriCount = layers.filter(l => l.layer_category === 'ori').length;

  // Filter and sort
  const filteredLayers = layers.filter(layer => {
    const matchesSearch = layer.layer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         layer.layer_code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || layer.layer_category === categoryFilter;

    return matchesSearch && matchesCategory;
  });

  const sortedLayers = [...filteredLayers].sort((a, b) => {
    const aValue = a[sortBy];
    const bValue = b[sortBy];

    if (aValue === null || aValue === undefined) return 1;
    if (bValue === null || bValue === undefined) return -1;

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortOrder === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    return sortOrder === 'asc'
      ? (aValue > bValue ? 1 : -1)
      : (aValue < bValue ? 1 : -1);
  });

  // Pagination
  const totalPages = Math.ceil(sortedLayers.length / itemsPerPage);
  const paginatedLayers = sortedLayers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleSort = (column: keyof BaseLayer) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const resetForm = () => {
    setFormData({
      layer_code: '',
      layer_name: '',
      layer_name_local: '',
      description: '',
      layer_type: 'tile',
      layer_category: 'street',
      tile_url: '',
      tile_subdomains: '',
      wms_url: '',
      wms_layers: '',
      wms_version: '1.1.1',
      wms_format: 'image/png',
      wms_transparent: true,
      attribution: '',
      max_zoom: 18,
      min_zoom: 0,
      is_default: false,
      is_multi_select_enabled: false,
      parent_layer_id: null,
      icon: 'ri-map-line',
      display_order: 0,
      sequence_id: 0,
      is_active: true
    });
    setEditingLayer(null);
    setIsSubmitting(false);
  };

  const handleEdit = (layer: BaseLayer) => {
    setEditingLayer(layer);
    setFormData({
      layer_code: layer.layer_code,
      layer_name: layer.layer_name,
      layer_name_local: layer.layer_name_local || '',
      description: layer.description || '',
      layer_type: layer.layer_type,
      layer_category: layer.layer_category,
      tile_url: layer.tile_url || '',
      tile_subdomains: Array.isArray(layer.tile_subdomains) ? layer.tile_subdomains.join(',') : '',
      wms_url: layer.wms_url || '',
      wms_layers: layer.wms_layers || '',
      wms_version: layer.wms_version || '1.1.1',
      wms_format: layer.wms_format || 'image/png',
      wms_transparent: layer.wms_transparent,
      attribution: layer.attribution || '',
      max_zoom: layer.max_zoom,
      min_zoom: layer.min_zoom,
      is_default: layer.is_default,
      is_multi_select_enabled: layer.is_multi_select_enabled,
      parent_layer_id: layer.parent_layer_id,
      icon: layer.icon,
      display_order: layer.display_order,
      sequence_id: layer.sequence_id,
      is_active: layer.is_active
    });
    setShowCreateModal(true);
  };

  const handleDelete = (layer: BaseLayer) => {
    setDeletingLayer(layer);
    setShowDeleteModal(true);
  };

  const handlePreview = (layer: BaseLayer) => {
    setPreviewLayer(layer);
    setShowPreviewModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setIsSubmitting(true);
      const submitData = {
        ...formData,
        tile_subdomains: formData.tile_subdomains ? formData.tile_subdomains.split(',').map(s => s.trim()) : null
      };

      if (editingLayer) {
        await gisService.updateBaseLayer(editingLayer.id, submitData);
        notificationService.success('Base layer updated successfully');
      } else {
        await gisService.createBaseLayer(submitData);
        notificationService.success('Base layer created successfully');
      }

      setShowCreateModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving base layer:', error);
      notificationService.error('Failed to save base layer');
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deletingLayer) return;

    try {
      await gisService.deleteBaseLayer(deletingLayer.id);
      notificationService.success('Base layer deleted successfully');
      setShowDeleteModal(false);
      setDeletingLayer(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting base layer:', error);
      notificationService.error('Failed to delete base layer');
    }
  };

  const handleSetDefault = async (layer: BaseLayer) => {
    if (window.confirm(`Set "${layer.layer_name}" as the default ${layer.layer_category} layer?`)) {
      try {
        await gisService.setDefaultBaseLayer(layer.id, layer.layer_category);
        notificationService.success(`"${layer.layer_name}" set as default ${layer.layer_category} layer`);
        fetchData();
      } catch (error) {
        console.error('Error setting default:', error);
        notificationService.error('Failed to set default layer');
      }
    }
  };

  const handleSequenceChange = async (layer: BaseLayer, direction: 'up' | 'down') => {
    const currentIndex = sortedLayers.findIndex(l => l.id === layer.id);
    if (direction === 'up' && currentIndex === 0) return;
    if (direction === 'down' && currentIndex === sortedLayers.length - 1) return;

    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    const targetLayer = sortedLayers[targetIndex];

    try {
      // Swap sequence_ids
      await gisService.updateBaseLayer(layer.id, { ...layer, sequence_id: targetLayer.sequence_id });
      await gisService.updateBaseLayer(targetLayer.id, { ...targetLayer, sequence_id: layer.sequence_id });
      notificationService.success('Layer sequence updated successfully');
      fetchData();
    } catch (error) {
      console.error('Error changing sequence:', error);
      notificationService.error('Failed to change sequence');
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'street': return 'ri-road-map-line';
      case 'satellite': return 'ri-satellite-line';
      case 'hybrid': return 'ri-stack-line';
      case 'terrain': return 'ri-landscape-line';
      case 'ori': return 'ri-government-line';
      default: return 'ri-map-line';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'street': return 'blue';
      case 'satellite': return 'green';
      case 'hybrid': return 'purple';
      case 'terrain': return 'yellow';
      case 'ori': return 'red';
      default: return 'gray';
    }
  };

  // Initialize Leaflet map for preview
  useEffect(() => {
    if (showPreviewModal && previewLayer && mapContainerRef.current) {
      // Clean up existing map if any
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }

      // Initialize new map
      const map = L.map(mapContainerRef.current, {
        center: [17.385, 78.486], // Default to Hyderabad, India
        zoom: 12,
        minZoom: previewLayer.min_zoom,
        maxZoom: previewLayer.max_zoom
      });

      // Add the base layer based on type
      if (previewLayer.layer_type === 'tile' && previewLayer.tile_url) {
        L.tileLayer(previewLayer.tile_url, {
          attribution: previewLayer.attribution || '',
          subdomains: previewLayer.tile_subdomains || [],
          maxZoom: previewLayer.max_zoom,
          minZoom: previewLayer.min_zoom
        }).addTo(map);
      } else if (previewLayer.layer_type === 'wms' && previewLayer.wms_url) {
        L.tileLayer.wms(previewLayer.wms_url, {
          layers: previewLayer.wms_layers || '',
          format: previewLayer.wms_format || 'image/png',
          transparent: previewLayer.wms_transparent,
          version: previewLayer.wms_version || '1.1.1',
          attribution: previewLayer.attribution || ''
        }).addTo(map);
      }

      mapInstanceRef.current = map;

      // Fix map sizing issue
      setTimeout(() => {
        map.invalidateSize();
      }, 100);
    }

    // Cleanup on unmount or when modal closes
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [showPreviewModal, previewLayer]);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Base Layers</h1>
        <p className="text-gray-600 dark:text-gray-400">Manage base map layer configurations</p>
      </div>

      {/* Category Stats Cards */}
      {!isLoading && layers.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          {/* Street Layers */}
          <div
            onClick={() => setCategoryFilter(categoryFilter === 'street' ? 'all' : 'street')}
            className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
              categoryFilter === 'street'
                ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500 ring-2 ring-blue-300'
                : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-road-map-line text-xl text-white"></i>
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Street</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{streetCount}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Satellite Layers */}
          <div
            onClick={() => setCategoryFilter(categoryFilter === 'satellite' ? 'all' : 'satellite')}
            className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
              categoryFilter === 'satellite'
                ? 'bg-green-50 dark:bg-green-900/20 border-green-500 ring-2 ring-green-300'
                : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-green-300 dark:hover:border-green-600'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-satellite-line text-xl text-white"></i>
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Satellite</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{satelliteCount}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Hybrid Layers */}
          <div
            onClick={() => setCategoryFilter(categoryFilter === 'hybrid' ? 'all' : 'hybrid')}
            className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
              categoryFilter === 'hybrid'
                ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-500 ring-2 ring-purple-300'
                : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-600'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-stack-line text-xl text-white"></i>
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Hybrid</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{hybridCount}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Terrain Layers */}
          <div
            onClick={() => setCategoryFilter(categoryFilter === 'terrain' ? 'all' : 'terrain')}
            className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
              categoryFilter === 'terrain'
                ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-500 ring-2 ring-yellow-300'
                : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-yellow-300 dark:hover:border-yellow-600'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-yellow-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-landscape-line text-xl text-white"></i>
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Terrain</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{terrainCount}</p>
                </div>
              </div>
            </div>
          </div>

          {/* ORI Layers - Highlighted */}
          <div
            onClick={() => setCategoryFilter(categoryFilter === 'ori' ? 'all' : 'ori')}
            className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
              categoryFilter === 'ori'
                ? 'bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/30 dark:to-red-800/30 border-red-500 ring-4 ring-red-300 shadow-lg'
                : 'bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 border-red-300 dark:border-red-700 hover:border-red-500 hover:shadow-md'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center mr-3 shadow-md">
                  <i className="ri-government-line text-xl text-white"></i>
                </div>
                <div>
                  <p className="text-xs font-semibold text-red-700 dark:text-red-300">ORI Layers</p>
                  <p className="text-lg font-bold text-red-900 dark:text-red-100">{oriCount}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div>
            <input
              type="text"
              placeholder="Search base layers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>

          {/* Category Filter */}
          <div>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              <option value="all">All Categories</option>
              <option value="street">Street</option>
              <option value="satellite">Satellite</option>
              <option value="hybrid">Hybrid</option>
              <option value="terrain">Terrain</option>
              <option value="ori">ORI</option>
            </select>
          </div>

          {/* Create Button */}
          <div>
            <button
              onClick={() => { resetForm(); setShowCreateModal(true); }}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
            >
              <i className="ri-add-line mr-2"></i>
              Create Layer
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        {isLoading ? (
          <div className="p-4"><TableSkeleton rows={6} showToolbar={false} /></div>
        ) : filteredLayers.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64">
            <i className="ri-map-line text-6xl text-gray-300 dark:text-gray-600 mb-4"></i>
            <p className="text-gray-500 dark:text-gray-400">No base layers found</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th
                      onClick={() => handleSort('sequence_id')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    >
                      <div className="flex items-center">
                        Order
                        {sortBy === 'sequence_id' && (
                          <i className={`ml-1 ri-arrow-${sortOrder === 'asc' ? 'up' : 'down'}-s-line`}></i>
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Icon
                    </th>
                    <th
                      onClick={() => handleSort('layer_name')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    >
                      <div className="flex items-center">
                        Layer Name
                        {sortBy === 'layer_name' && (
                          <i className={`ml-1 ri-arrow-${sortOrder === 'asc' ? 'up' : 'down'}-s-line`}></i>
                        )}
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('layer_category')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    >
                      <div className="flex items-center">
                        Category
                        {sortBy === 'layer_category' && (
                          <i className={`ml-1 ri-arrow-${sortOrder === 'asc' ? 'up' : 'down'}-s-line`}></i>
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Default
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {paginatedLayers.map((layer, index) => {
                    const categoryColor = getCategoryColor(layer.layer_category);
                    const globalIndex = (currentPage - 1) * itemsPerPage + index;
                    return (
                      <tr key={layer.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-1">
                            <span className="text-sm text-gray-900 dark:text-gray-100 mr-2">{layer.sequence_id}</span>
                            <button
                              onClick={() => handleSequenceChange(layer, 'up')}
                              disabled={globalIndex === 0}
                              className="p-1 text-gray-400 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed"
                              title="Move up"
                            >
                              <i className="ri-arrow-up-s-line text-lg"></i>
                            </button>
                            <button
                              onClick={() => handleSequenceChange(layer, 'down')}
                              disabled={globalIndex === sortedLayers.length - 1}
                              className="p-1 text-gray-400 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed"
                              title="Move down"
                            >
                              <i className="ri-arrow-down-s-line text-lg"></i>
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <i className={`${getCategoryIcon(layer.layer_category)} text-2xl text-${categoryColor}-600 dark:text-${categoryColor}-400`}></i>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{layer.layer_name}</div>
                          {layer.layer_name_local && (
                            <div className="text-sm text-gray-500 dark:text-gray-400">{layer.layer_name_local}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-${categoryColor}-100 text-${categoryColor}-800 dark:bg-${categoryColor}-900/20 dark:text-${categoryColor}-400`}>
                            {layer.layer_category.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          {layer.is_default ? (
                            <i className="ri-check-line text-green-600 dark:text-green-400 text-xl"></i>
                          ) : (
                            <button
                              onClick={() => handleSetDefault(layer)}
                              className="text-gray-400 hover:text-green-600 dark:hover:text-green-400"
                              title="Set as default"
                            >
                              <i className="ri-checkbox-blank-circle-line text-xl"></i>
                            </button>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => handlePreview(layer)}
                            className="text-purple-600 hover:text-purple-900 dark:text-purple-400 dark:hover:text-purple-300 mr-3"
                            title="Preview"
                          >
                            <i className="ri-eye-line"></i>
                          </button>
                          <button
                            onClick={() => handleEdit(layer)}
                            className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 mr-3"
                            title="Edit"
                          >
                            <i className="ri-edit-line"></i>
                          </button>
                          <button
                            onClick={() => handleDelete(layer)}
                            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                            title="Delete"
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200 dark:border-gray-700">
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, sortedLayers.length)} of {sortedLayers.length} layers
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {editingLayer ? 'Edit Base Layer' : 'Create Base Layer'}
              </h2>
              <button
                onClick={() => { setShowCreateModal(false); resetForm(); }}
                disabled={isSubmitting}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="space-y-6">
                {/* Basic Information */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Basic Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Layer Code *
                      </label>
                      <input
                        type="text"
                        value={formData.layer_code}
                        onChange={(e) => setFormData({ ...formData, layer_code: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Layer Name *
                      </label>
                      <input
                        type="text"
                        value={formData.layer_name}
                        onChange={(e) => setFormData({ ...formData, layer_name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Local Name
                      </label>
                      <input
                        type="text"
                        value={formData.layer_name_local}
                        onChange={(e) => setFormData({ ...formData, layer_name_local: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Icon (RemixIcon)
                      </label>
                      <input
                        type="text"
                        value={formData.icon}
                        onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                        placeholder="ri-map-line"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>

                {/* Layer Configuration */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Layer Configuration</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Layer Type
                      </label>
                      <select
                        value={formData.layer_type}
                        onChange={(e) => setFormData({ ...formData, layer_type: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      >
                        <option value="tile">Tile</option>
                        <option value="wms">WMS</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Category
                      </label>
                      <select
                        value={formData.layer_category}
                        onChange={(e) => setFormData({ ...formData, layer_category: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      >
                        <option value="street">Street</option>
                        <option value="satellite">Satellite</option>
                        <option value="hybrid">Hybrid</option>
                        <option value="terrain">Terrain</option>
                        <option value="ori">ORI</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Tile Configuration */}
                {formData.layer_type === 'tile' && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Tile Configuration</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Tile URL
                        </label>
                        <input
                          type="text"
                          value={formData.tile_url}
                          onChange={(e) => setFormData({ ...formData, tile_url: e.target.value })}
                          placeholder={TILE_URLS.osmPlain}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Tile Subdomains (comma-separated)
                        </label>
                        <input
                          type="text"
                          value={formData.tile_subdomains}
                          onChange={(e) => setFormData({ ...formData, tile_subdomains: e.target.value })}
                          placeholder="a,b,c"
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* WMS Configuration */}
                {formData.layer_type === 'wms' && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">WMS Configuration</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          WMS URL
                        </label>
                        <input
                          type="text"
                          value={formData.wms_url}
                          onChange={(e) => setFormData({ ...formData, wms_url: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            WMS Layers
                          </label>
                          <input
                            type="text"
                            value={formData.wms_layers}
                            onChange={(e) => setFormData({ ...formData, wms_layers: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            WMS Version
                          </label>
                          <input
                            type="text"
                            value={formData.wms_version}
                            onChange={(e) => setFormData({ ...formData, wms_version: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            WMS Format
                          </label>
                          <select
                            value={formData.wms_format}
                            onChange={(e) => setFormData({ ...formData, wms_format: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                          >
                            <option value="image/png">PNG</option>
                            <option value="image/jpeg">JPEG</option>
                            <option value="image/png8">PNG8</option>
                          </select>
                        </div>

                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="wms_transparent"
                            checked={formData.wms_transparent}
                            onChange={(e) => setFormData({ ...formData, wms_transparent: e.target.checked })}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <label htmlFor="wms_transparent" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                            Transparent
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Other Settings */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Other Settings</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Min Zoom
                      </label>
                      <input
                        type="number"
                        value={formData.min_zoom}
                        onChange={(e) => setFormData({ ...formData, min_zoom: parseInt(e.target.value) })}
                        min="0"
                        max="22"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Max Zoom
                      </label>
                      <input
                        type="number"
                        value={formData.max_zoom}
                        onChange={(e) => setFormData({ ...formData, max_zoom: parseInt(e.target.value) })}
                        min="0"
                        max="22"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Sequence ID
                      </label>
                      <input
                        type="number"
                        value={formData.sequence_id}
                        onChange={(e) => setFormData({ ...formData, sequence_id: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Attribution
                    </label>
                    <input
                      type="text"
                      value={formData.attribution}
                      onChange={(e) => setFormData({ ...formData, attribution: e.target.value })}
                      placeholder="© OpenStreetMap contributors"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  <div className="mt-4 flex gap-6">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="is_default"
                        checked={formData.is_default}
                        onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <label htmlFor="is_default" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                        Set as Default
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="is_active"
                        checked={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <label htmlFor="is_active" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                        Active
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => { setShowCreateModal(false); resetForm(); }}
                  disabled={isSubmitting}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSubmitting && (
                    <i className="ri-loader-4-line animate-spin"></i>
                  )}
                  {editingLayer ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deletingLayer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
                <i className="ri-error-warning-line text-2xl text-red-600 dark:text-red-400"></i>
              </div>
              <h3 className="ml-4 text-lg font-semibold text-gray-900 dark:text-white">
                Delete Base Layer
              </h3>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete <span className="font-semibold">{deletingLayer.layer_name}</span>? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setShowDeleteModal(false); setDeletingLayer(null); }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && previewLayer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Layer Preview: {previewLayer.layer_name}
              </h2>
              <button
                onClick={() => { setShowPreviewModal(false); setPreviewLayer(null); }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="p-6">
              {/* Preview Info */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-semibold text-gray-700 dark:text-gray-300">Type:</span>
                    <span className="ml-2 text-gray-600 dark:text-gray-400">{previewLayer.layer_type.toUpperCase()}</span>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-700 dark:text-gray-300">Category:</span>
                    <span className="ml-2 text-gray-600 dark:text-gray-400">{previewLayer.layer_category.toUpperCase()}</span>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-700 dark:text-gray-300">Zoom Range:</span>
                    <span className="ml-2 text-gray-600 dark:text-gray-400">{previewLayer.min_zoom} - {previewLayer.max_zoom}</span>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-700 dark:text-gray-300">Default:</span>
                    <span className="ml-2 text-gray-600 dark:text-gray-400">{previewLayer.is_default ? 'Yes' : 'No'}</span>
                  </div>
                </div>
              </div>

              {/* URL/Configuration */}
              <div className="space-y-3">
                {previewLayer.layer_type === 'tile' && previewLayer.tile_url && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Tile URL:</label>
                    <code className="block p-2 bg-gray-100 dark:bg-gray-900 rounded text-sm text-gray-800 dark:text-gray-200 overflow-x-auto">
                      {previewLayer.tile_url}
                    </code>
                  </div>
                )}

                {previewLayer.layer_type === 'wms' && previewLayer.wms_url && (
                  <>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">WMS URL:</label>
                      <code className="block p-2 bg-gray-100 dark:bg-gray-900 rounded text-sm text-gray-800 dark:text-gray-200 overflow-x-auto">
                        {previewLayer.wms_url}
                      </code>
                    </div>
                    {previewLayer.wms_layers && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">WMS Layers:</label>
                        <code className="block p-2 bg-gray-100 dark:bg-gray-900 rounded text-sm text-gray-800 dark:text-gray-200">
                          {previewLayer.wms_layers}
                        </code>
                      </div>
                    )}
                  </>
                )}

                {previewLayer.attribution && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Attribution:</label>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{previewLayer.attribution}</p>
                  </div>
                )}
              </div>

              {/* Map Preview */}
              <div className="mt-6">
                <div
                  ref={mapContainerRef}
                  className="w-full h-96 rounded-lg border border-gray-300 dark:border-gray-600"
                  style={{ minHeight: '400px' }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BaseLayers;
